import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, UserPlus, Users, Crown, Trash2, Edit, Loader2, ShieldCheck, ShieldAlert, Mail, Lock, Eye, EyeOff } from 'lucide-react';

const API_URL = window.location.origin;

const AdminManagementPage = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // New admin form
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    full_name: '',
    password: '',
    admin_role: 'admin'
  });

  // Check if current user is super admin
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
  }, [isSuperAdmin]);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/admins`);
      setAdmins(response.data);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdmin.email || !newAdmin.full_name || !newAdmin.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newAdmin.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/admins`, newAdmin);
      toast.success('Admin created successfully!');
      setShowAddDialog(false);
      setNewAdmin({ email: '', full_name: '', password: '', admin_role: 'admin' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAdmin = async () => {
    if (!selectedAdmin) return;

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/admins/${selectedAdmin.id}`, {
        admin_role: selectedAdmin.role,
        is_active: selectedAdmin.access_status === 'active'
      });
      toast.success('Admin updated successfully!');
      setShowEditDialog(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAdmin = async (adminId, adminEmail) => {
    if (!confirm(`Remove admin privileges from ${adminEmail}?`)) return;

    try {
      await axios.delete(`${API_URL}/api/admin/admins/${adminId}`);
      toast.success('Admin privileges removed');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="card-base">
            <CardContent className="p-12 text-center">
              <ShieldAlert className="h-16 w-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-heading text-forest mb-2">Access Denied</h2>
              <p className="text-gray-600">Only Super Admins can access this page.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl text-forest mb-2 flex items-center gap-3">
            <Shield className="h-8 w-8 text-terracotta" />
            Admin Management
          </h1>
          <p className="text-gray-600">Manage administrator accounts and permissions</p>
        </div>

        {/* Add Admin Button */}
        <div className="mb-6">
          <Button onClick={() => setShowAddDialog(true)} className="btn-primary">
            <UserPlus className="h-4 w-4 mr-2" />
            Add New Admin
          </Button>
        </div>

        {/* Admin List */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Users className="h-5 w-5 text-forest" />
              Current Admins
            </CardTitle>
            <CardDescription>
              {admins.length} admin{admins.length !== 1 ? 's' : ''} total
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-forest" />
              </div>
            ) : admins.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No admins found</p>
            ) : (
              <div className="space-y-4">
                {admins.map((admin) => (
                  <div 
                    key={admin.id} 
                    className={`flex items-center justify-between p-4 rounded-xl border ${
                      admin.role === 'super_admin' 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        admin.role === 'super_admin' ? 'bg-amber-100' : 'bg-forest/10'
                      }`}>
                        {admin.role === 'super_admin' ? (
                          <Crown className="h-6 w-6 text-amber-600" />
                        ) : (
                          <ShieldCheck className="h-6 w-6 text-forest" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-forest">{admin.full_name}</h3>
                        <p className="text-sm text-gray-500">{admin.email}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge className={
                        admin.role === 'super_admin' 
                          ? 'bg-amber-100 text-amber-800 border-amber-300' 
                          : 'bg-forest/10 text-forest border-forest/20'
                      }>
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </Badge>
                      
                      {admin.access_status === 'suspended' && (
                        <Badge variant="destructive">Suspended</Badge>
                      )}

                      {admin.id !== user.id && (
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedAdmin({ ...admin });
                              setShowEditDialog(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}

                      {admin.id === user.id && (
                        <Badge variant="outline" className="text-gray-500">You</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role Explanation */}
        <Card className="card-base mt-6">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Role Permissions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
              <Crown className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800">Super Admin</h4>
                <p className="text-sm text-amber-700">Full access to all features including managing other admins</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-forest/5 rounded-lg">
              <ShieldCheck className="h-5 w-5 text-forest mt-0.5" />
              <div>
                <h4 className="font-medium text-forest">Admin</h4>
                <p className="text-sm text-gray-600">Can manage content (videos, resources) and view reports/analytics</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-forest" />
              Add New Admin
            </DialogTitle>
            <DialogDescription>
              Create a new administrator account
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="adminName">Full Name</Label>
              <Input
                id="adminName"
                value={newAdmin.full_name}
                onChange={(e) => setNewAdmin({ ...newAdmin, full_name: e.target.value })}
                placeholder="Enter full name"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="adminEmail">Email</Label>
              <Input
                id="adminEmail"
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                placeholder="Enter email address"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="adminPassword">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="adminPassword"
                  type={showPassword ? "text" : "password"}
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder="Min 8 characters"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label>Admin Role</Label>
              <Select
                value={newAdmin.admin_role}
                onValueChange={(value) => setNewAdmin({ ...newAdmin, admin_role: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <span className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4" />
                      Admin (Content & Reports)
                    </span>
                  </SelectItem>
                  <SelectItem value="super_admin">
                    <span className="flex items-center gap-2">
                      <Crown className="h-4 w-4" />
                      Super Admin (Full Access)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAdmin} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Edit className="h-5 w-5 text-forest" />
              Edit Admin
            </DialogTitle>
            <DialogDescription>
              Update admin role and status
            </DialogDescription>
          </DialogHeader>

          {selectedAdmin && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedAdmin.full_name}</p>
                <p className="text-sm text-gray-500">{selectedAdmin.email}</p>
              </div>

              <div>
                <Label>Admin Role</Label>
                <Select
                  value={selectedAdmin.role}
                  onValueChange={(value) => setSelectedAdmin({ ...selectedAdmin, role: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <span className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4" />
                        Admin (Content & Reports)
                      </span>
                    </SelectItem>
                    <SelectItem value="super_admin">
                      <span className="flex items-center gap-2">
                        <Crown className="h-4 w-4" />
                        Super Admin (Full Access)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select
                  value={selectedAdmin.access_status || 'active'}
                  onValueChange={(value) => setSelectedAdmin({ ...selectedAdmin, access_status: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAdmin} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Edit className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminManagementPage;
